/**
 * The postMessage contract between the app and a sandboxed figure.
 *
 * The frame runs at an *opaque* origin (`sandbox="allow-scripts"` with no
 * `allow-same-origin`), which has two consequences this file exists to make
 * explicit:
 *
 *  - The app cannot name a target origin when posting into it, so it must post
 *    to `"*"`. That is only safe because nothing here is a secret: the payload
 *    is the model's own sketch plus theme colours, both already on screen.
 *  - Messages coming *out* carry `origin: "null"`, so the app authenticates
 *    them by comparing `event.source` against the frame's `contentWindow`
 *    rather than by origin.
 *
 * `runner.js` speaks this protocol with string literals — it is plain browser
 * JavaScript served as a static asset and cannot import this module — so a test
 * pins the two together.
 */

export const ANIM_PROTOCOL_VERSION = 1;

export type ThemeMode = "paper" | "night";

/** Colours handed to the sketch, so a figure is never light-on-white at night. */
export interface AnimTheme {
  readonly mode: ThemeMode;
  readonly surface: string;
  readonly ink: string;
  readonly muted: string;
  readonly grid: string;
  readonly axis: string;
  readonly series: readonly string[];
}

/** App → frame. */
export type AnimCommand =
  | {
      readonly v: typeof ANIM_PROTOCOL_VERSION;
      readonly type: "run";
      readonly code: string;
      readonly theme: AnimTheme;
      readonly reducedMotion: boolean;
    }
  | {
      readonly v: typeof ANIM_PROTOCOL_VERSION;
      readonly type: "setPlaying";
      readonly playing: boolean;
    }
  | {
      readonly v: typeof ANIM_PROTOCOL_VERSION;
      readonly type: "theme";
      readonly theme: AnimTheme;
    };

/** Frame → app. */
export type AnimEvent =
  | { readonly v: typeof ANIM_PROTOCOL_VERSION; readonly type: "ready" }
  | {
      readonly v: typeof ANIM_PROTOCOL_VERSION;
      readonly type: "state";
      readonly playing: boolean;
    }
  | {
      readonly v: typeof ANIM_PROTOCOL_VERSION;
      readonly type: "error";
      readonly message: string;
    };

const EVENT_TYPES = new Set(["ready", "state", "error"]);

/**
 * Narrow an untrusted `MessageEvent.data`.
 *
 * The frame shares its window with model-authored code, so even a message that
 * really did come from the right `source` is not automatically well-formed.
 */
export function isAnimEvent(data: unknown): data is AnimEvent {
  if (typeof data !== "object" || data === null) return false;

  const candidate = data as Record<string, unknown>;
  if (candidate.v !== ANIM_PROTOCOL_VERSION) return false;
  if (typeof candidate.type !== "string") return false;
  if (!EVENT_TYPES.has(candidate.type)) return false;

  if (candidate.type === "state") return typeof candidate.playing === "boolean";
  if (candidate.type === "error") return typeof candidate.message === "string";
  return true;
}
